//libraries imports
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
//Core Components
import Loader from "../../../../core/common/loader";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import { all_routes } from "../../../router/all_routes";
import { DatePicker, Input, Select } from "antd";
import { Dayjs } from "dayjs";

//Other Components
import StatsCard from "./components/StatsCard";
import Pagination from "../../common/components/Pagination";
import UpcomingReportCard from "./components/UpcomingReportCard";
import AllReportsCard from "./components/AllReportsCard";

// Helpers & Types
import api from "../../../../services/api";
import { notify } from "../../../../services/toast.config";
import { ReportType, BackendAllReport } from "./types";
import { toProperCase } from "../../../../helper/textHelper";
import QuickInfoModal from "../../common/components/QuickInfoModal";
import { humanReadableDate } from "../../../../helper/dateTimeHelper";

const { Search } = Input;

interface AllReportsPaginationData {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  startIndex: number;
  endIndex: number;
}

interface AllReportsFilters {
  search_term: string | null;
  report_type_id: number | null;
  report_frequency: string | null;
  start_date: Dayjs | null;
  end_date: Dayjs | null;
  sort_by: string | null;
}

const DEFAULT_FILTERS: AllReportsFilters = {
  search_term: null,
  report_type_id: null,
  report_frequency: null,
  start_date: null,
  end_date: null,
  sort_by: null,
};

const MyReports: React.FC = () => {
  // LOCAL STORAGE
  const signature: string | null = localStorage.getItem("signature");

  // STATE MANAGEMENT
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [allReports, setAllReports] = useState<BackendAllReport[]>([]);
  const [stats, setStats] = useState<{ summary: any; frequency: any } | any>(
    {}
  );

  // Upcoming Reports - Frontend Pagination
  const [upcomingReports, setUpcomingReports] = useState<ReportType[]>([]);
  const [upcomingCurrentPage, setUpcomingCurrentPage] = useState<number>(1);
  const upcomingReportsPerPage = 3;

  // All reports - Backend pagination
  const [allReportsPaginationData, setAllReportsPaginationData] =
    useState<AllReportsPaginationData>({
      currentPage: 1,
      totalPages: 0,
      totalItems: 0,
      itemsPerPage: 10,
      hasNextPage: false,
      hasPrevPage: false,
      startIndex: 1,
      endIndex: 0,
    });

  const [modalInfo, setModalInfo] = useState("");
  const [modalData, setModalData] = useState<any[]>([]);
  const [modalDataColumns, setmodalDataColumns] = useState<any[]>([]);

  // All reports filters
  const [allReportsFilters, setAllReportsFilters] =
    useState<AllReportsFilters>(DEFAULT_FILTERS);

  // Filter states for All Reports
  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const [reportFrequency, setReportFrequency] = useState<string | null>(null);
  const [reportTypeFilter, setReportTypeFilter] = useState<number | null>(null);
  const [reportTypes, setReportTypes] = useState<any>([]);
  const [reportsStartDate, setReportsStartDate] = useState<Dayjs | null>(null);
  const [reportsEndDate, setReportsEndDate] = useState<Dayjs | null>(null);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [allUsers, setAllUsers] = useState<any>([]);
  const [reportsSortBy, setReportsSortBy] = useState<string | null>(null);

  const filteredUpcomingReports = upcomingReports;
  const totalFilteredReports = filteredUpcomingReports.length;
  const upcomingTotalPages = Math.ceil(
    totalFilteredReports / upcomingReportsPerPage
  );
  const upcomingStartIndex = (upcomingCurrentPage - 1) * upcomingReportsPerPage;
  const upcomingEndIndex = upcomingStartIndex + upcomingReportsPerPage;

  const paginatedUpcomingReports = filteredUpcomingReports.slice(
    upcomingStartIndex,
    upcomingEndIndex
  );

  // Count active filters
  const activeFiltersCount = [
    searchTerm,
    reportFrequency,
    reportTypeFilter,
    reportsStartDate,
    reportsEndDate,
    reportsSortBy,
  ].filter((filter) => filter !== null).length;

  // Clear individual filters
  const clearSearchTerm = () => setSearchTerm(null);
  const clearReportFrequency = () => setReportFrequency(null);
  const clearReportTypeFilter = () => setReportTypeFilter(null);
  const clearStartDateFilters = () => setReportsStartDate(null);
  const clearEndDateFilters = () => setReportsEndDate(null);
  const clearSortBy = () => setReportsSortBy(null);

  // HANDLERS

  // All Reports Filter Handlers
  const handleAllReportsSearch = () => {
    const filters: AllReportsFilters = {
      search_term: searchTerm?.trim() || null,
      report_type_id: reportTypeFilter || null,
      report_frequency: reportFrequency || null,
      start_date: reportsStartDate || null,
      end_date: reportsEndDate || null,
      sort_by: reportsSortBy || null,
    };
    setAllReportsFilters(filters);
    loadAllReports(1, filters);
    fetchUserReportStatistics(filters);
    fetchUpcomingReports(filters);
  };

  const handleClearAllReportsFilters = () => {
    setSearchTerm(null);
    setReportFrequency(null);
    setReportTypeFilter(null);
    setReportsStartDate(null);
    setReportsEndDate(null);
    setReportsSortBy(null);
    setAllReportsFilters(DEFAULT_FILTERS);
    loadAllReports(1, DEFAULT_FILTERS);
    fetchUserReportStatistics(DEFAULT_FILTERS);
    fetchUpcomingReports(DEFAULT_FILTERS);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const reportsSortOptions = [
    {
      value: "report_date-ASC",
      label: "Report Date [Old - New]",
    },
    {
      value: "report_date-DESC",
      label: "Report Date [New - Old]",
    },
    {
      value: "submitted_at-DESC",
      label: "Submission Date [New - Old]",
    },
    {
      value: "submitted_at-ASC",
      label: "Submission Date [Old - New]",
    },
    {
      value: "report_type_id-ASC",
      label: "Report Type [Ascending]",
    },
    {
      value: "report_type_id-DESC",
      label: "Report Type [DESC]",
    },
  ];

  // EFFECTS
  useEffect(() => {
    fetchUpcomingReports();
    fetchUserReportStatistics();
    fetchAllReportTypes();
    loadAllReports(1);
    fetchAllUsers();
  }, []);

  // API HANDLERS
  const fetchUpcomingReports = async (filters: AllReportsFilters = allReportsFilters): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await api.post("ers/reports/getUpcomingReportTypes", {
        signature: signature,
        start_date: filters.start_date,
        end_date: filters.end_date,
      });

      const reportData = response.data?.data?.pending_reports || [];

      const validReports = reportData?.filter(
        (report: ReportType) =>
          report.due_date !== null && report.due_date !== undefined
      );

      setUpcomingReports(validReports);
    } catch (e: any) {
      const error = e?.response?.data || e?.data;
      let errorMessage = "Failed to load upcoming reports!";
      if (Array.isArray(error?.message) && error?.message[0]?.msg) {
        errorMessage = error.message[0].msg;
      } else if (typeof error?.message === "string" && error.message.trim()) {
        errorMessage = error.message;
      }
      notify.error(errorMessage);
      setUpcomingReports([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllReportTypes = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const rawResponse = await api.post("ers/reports/getAllReportTypes", {
        signature: signature,
      });

      const respone = rawResponse?.data?.data || [];
      setReportTypes(respone);
    } catch (e: any) {
      const error = e?.response?.data || e?.data;
      let errorMessage = "Failed to load  report type!";
      if (Array.isArray(error?.message) && error?.message[0]?.msg) {
        errorMessage = error.message[0].msg;
      } else if (typeof error?.message === "string" && error.message.trim()) {
        errorMessage = error.message;
      }
      notify.error(errorMessage);
      setReportTypes([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserReportStatistics = async (
    filters: AllReportsFilters = allReportsFilters
  ): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await api.post("ers/reports/getUserReportStatistics", {
        signature: signature,
        start_date: filters.start_date,
        end_date: filters.end_date,
        report_type_id: filters.report_type_id,
        report_frequency: filters.report_frequency,
        search_term: filters.search_term,
      });

      const statsData = response.data?.data || {};
      setStats(statsData);
    } catch (e: any) {
      const error = e?.response?.data || e?.data;
      let errorMessage = "Failed to load stats!";
      if (Array.isArray(error?.message) && error?.message[0]?.msg) {
        errorMessage = error.message[0].msg;
      } else if (typeof error?.message === "string" && error.message.trim()) {
        errorMessage = error.message;
      }
      notify.error(errorMessage);
      setStats({});
    } finally {
      setIsLoading(false);
    }
  };

  // Updated loadAllReports with backend API call
  const loadAllReports = async (
    page: number,
    filters: AllReportsFilters = allReportsFilters
  ): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await api.post("ers/reports/getAllUserReports", {
        signature: signature,
        page: page,
        limit: allReportsPaginationData.itemsPerPage,
        ...filters,
      });

      const responseData = response.data?.data || {};
      const reportsData = responseData.reports || [];
      const paginationData = responseData.pagination || {};

      setAllReports(reportsData);
      setAllReportsPaginationData(paginationData);
    } catch (error: any) {
      const errorData = error?.response?.data || error?.data;
      let errorMessage = "Failed to load reports";

      if (Array.isArray(errorData?.message) && errorData?.message[0]?.msg) {
        errorMessage = errorData.message[0].msg;
      } else if (
        typeof errorData?.message === "string" &&
        errorData.message.trim()
      ) {
        errorMessage = errorData.message;
      }

      notify.error(errorMessage);
      setAllReports([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllUsers = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await api.post("ers/reports/getAllUsersOfCompany", {
        signature: signature,
      });

      const responseData = response.data?.data || [];

      setAllUsers(responseData);
    } catch (error: any) {
      const errorData = error?.response?.data || error?.data;
      let errorMessage = "Failed to load users";

      if (Array.isArray(errorData?.message) && errorData?.message[0]?.msg) {
        errorMessage = errorData.message[0].msg;
      } else if (
        typeof errorData?.message === "string" &&
        errorData.message.trim()
      ) {
        errorMessage = errorData.message;
      }

      notify.error(errorMessage);
      setAllReports([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReportDelete = async (report_id: number | any): Promise<void> => {
    try {
      if (!report_id) {
        throw new Error("Report not selected!");
      }
      const dataToSend = {
        signature: signature,
        report_id: report_id,
      };

      const response = await api.post("/ers/reports/deleteReport", dataToSend);
      notify.success(response?.data?.message || "Report deleted successfully");
      handleRefreshAllReports();
    } catch (e: any) {
      const error = e?.data;
      let errorMessage;
      if (Array.isArray(error?.message) && error?.message[0]?.msg) {
        errorMessage = error.message[0].msg;
      } else if (typeof error?.message === "string" && error.message.trim()) {
        errorMessage = error.message;
      } else {
        errorMessage = "Internal Server Error!";
      }
      notify.error(errorMessage);
    }
  };

  const onCreateTemplate = async (report_id: number | any): Promise<void> => {
    try {
      if (!report_id) {
        throw new Error("Report not selected!");
      }
      const dataToSend = {
        signature: signature,
        report_id: report_id,
      };

      const response = await api.post(
        "/ers/reports/createTemplate",
        dataToSend
      );
      notify.success(response?.data?.message || "Template added successfully");
    } catch (e: any) {
      const error = e?.data;
      let errorMessage;
      if (Array.isArray(error?.message) && error?.message[0]?.msg) {
        errorMessage = error.message[0].msg;
      } else if (typeof error?.message === "string" && error.message.trim()) {
        errorMessage = error.message;
      } else {
        errorMessage = "Internal Server Error!";
      }
      notify.error(errorMessage);
    }
  };

  const handleUpcomingPageChange = (page: number): void => {
    setUpcomingCurrentPage(page);
  };

  const handleAllReportsPageChange = (page: number): void => {
    loadAllReports(page);
  };

  const handleRefreshAllReports = (): void => {
    loadAllReports(allReportsPaginationData.currentPage);
    fetchUserReportStatistics();
  };

  // Stats Table Data
  const total_reports_columns = [
    {
      title: "Report Type",
      dataIndex: "frequency_type",
      render: (text: string) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-info-transparent py-2 p-2 table-text-wrap">
          {toProperCase(text)}
        </h6>
      ),
      sorter: (a: any, b: any) =>
        (a?.frequency_type || "").localeCompare(b?.frequency_type || ""),
    },
    {
      title: "Submitted On Time",
      dataIndex: "submitted_on_time",
      render: (text: string | number) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-success py-2 px-4">
          {text}
        </h6>
      ),
      sorter: (a: any, b: any) => a?.submitted_on_time - b?.submitted_on_time,
    },
    {
      title: "Submitted Late",
      dataIndex: "submitted_late",
      render: (text: string | number) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-danger-transparent py-2 px-4">
          {text}
        </h6>
      ),
      sorter: (a: any, b: any) => a?.submitted_late - b?.submitted_late,
    },
    {
      title: "Total Submitted",
      dataIndex: "total_reports",
      render: (text: string | number) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-secondary-transparent py-2 px-4">
          {text}
        </h6>
      ),
      sorter: (a: any, b: any) => a?.total_reports - b?.total_reports,
    },
    {
      title: "Overdue Reports",
      dataIndex: "overdue_reports",
      render: (text: string | number) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-danger-transparent py-2 px-4">
          {text}
        </h6>
      ),
      sorter: (a: any, b: any) => a?.overdue_reports - b?.overdue_reports,
    },
  ];

  const submitted_on_time_columns = [
    {
      title: "Report Type",
      dataIndex: "report_type_name",
      render: (text: string) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-info-transparent py-2 p-2 table-text-wrap">
          {text}
        </h6>
      ),
      sorter: (a: any, b: any) =>
        (a?.report_type_name || "").localeCompare(b?.report_type_name || ""),
    },
    {
      title: "Report Date",
      dataIndex: "report_date",
      sorter: (a: any, b: any) =>
        new Date(a?.report_date).getTime() - new Date(b?.report_date).getTime(),
      render: (text: string) => (
        <span className="badge d-inline-flex align-items-center badge-sm badge-secondary-transparent">
          {humanReadableDate(text)}
        </span>
      ),
    },
    {
      title: "Submission Date",
      dataIndex: "submitted_date",
      sorter: (a: any, b: any) =>
        new Date(a?.submitted_date).getTime() -
        new Date(b?.submitted_date).getTime(),
      render: (text: string) => (
        <span className="badge d-inline-flex align-items-center badge-sm badge-soft-success">
          {humanReadableDate(text)}
        </span>
      ),
    },
  ];

  const submitted_late_columns = [
    {
      title: "Report Type",
      dataIndex: "report_type_name",
      render: (text: string) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-info-transparent py-2 p-2 table-text-wrap">
          {text}
        </h6>
      ),
      sorter: (a: any, b: any) =>
        (a?.report_type_name || "").localeCompare(b?.report_type_name || ""),
    },
    {
      title: "Report Date",
      dataIndex: "report_date",
      sorter: (a: any, b: any) =>
        new Date(a?.report_date).getTime() - new Date(b?.report_date).getTime(),
      render: (text: string) => (
        <span className="badge d-inline-flex align-items-center badge-sm badge-secondary-transparent">
          {humanReadableDate(text)}
        </span>
      ),
    },
    {
      title: "Submission Date",
      dataIndex: "submitted_date",
      sorter: (a: any, b: any) =>
        new Date(a?.submitted_date).getTime() -
        new Date(b?.submitted_date).getTime(),
      render: (text: string) => (
        <span className="badge d-inline-flex align-items-center badge-sm badge-soft-success">
          {humanReadableDate(text)}
        </span>
      ),
    },
    {
      title: "Late By",
      dataIndex: "days_difference",
      sorter: (a: any, b: any) => a.days_difference - b.days_difference,
      render: (text: number) => (
        <span className="badge d-inline-flex align-items-center badge-sm badge-danger px-4">
          {text}
          {text <= 1 ? " Day" : " Days"}
        </span>
      ),
    },
  ];

  const overdue_reports_columns = [
    {
      title: "Report Type",
      dataIndex: "report_type_name",
      render: (text: string) => (
        <h6 className="fs-13 fw-medium badge d-inline-flex align-items-center badge-sm badge-info-transparent py-2 p-2 table-text-wrap">
          {text}
        </h6>
      ),
      sorter: (a: any, b: any) =>
        (a?.report_type_name || "").localeCompare(b?.report_type_name || ""),
    },
    {
      title: "Report Date",
      dataIndex: "report_date",
      sorter: (a: any, b: any) =>
        new Date(a?.report_date).getTime() - new Date(b?.report_date).getTime(),
      render: (text: string) => (
        <span className="badge d-inline-flex align-items-center badge-sm badge-secondary-transparent">
          {humanReadableDate(text)}
        </span>
      ),
    },
    {
      title: "Overdue By",
      dataIndex: "days_overdue",
      sorter: (a: any, b: any) => a.days_overdue - b.days_overdue,
      render: (text: number) => (
        <span className="badge d-inline-flex align-items-center badge-sm badge-danger px-4">
          {text} {text <= 1 ? "Day" : "Days"}
        </span>
      ),
    },
  ];

  //Card and numbers click
  const handleStatsCardClick = (category: string) => {
    let title = "";
    let data: any[] = [];
    let columns: any = [];

    switch (category) {
      case "total-stats":
        title = "Total Submitted Reports";
        data = stats?.frequency_breakdown;
        columns = total_reports_columns;
        break;

      case "submitted-on-time-reports":
        title = "On-Time Submissions";
        data = stats?.report_details?.submitted_on_time;
        columns = submitted_on_time_columns;
        break;

      case "submitted-late-reports":
        title = "Late Submissions";
        data = stats?.report_details?.submitted_late;
        columns = submitted_late_columns;
        break;

      case "overdue-reports":
        title = "Overdue Reports";
        data = stats?.report_details?.overdue_reports;
        columns = overdue_reports_columns;
        break;

      default:
        title = "";
        data = [];
        columns = [];
        break;
    }

    setModalInfo(title);
    setModalData(data);
    setmodalDataColumns(columns);
  };

  // Clear Filter Button Component
  const ClearFilterBtn = ({
    onClick,
    title = "Clear this filter",
  }: {
    onClick: () => void;
    title?: string;
  }) => (
    <button
      className="btn btn-link btn-sm p-0 text-danger text-decoration-none"
      onClick={onClick}
      title={title}
      style={{ fontSize: "12px", marginLeft: "8px" }}>
      <i className="ti ti-x fs-13 me-1" />
      Clear
    </button>
  );

  // RENDER COMPONENT
  return (
    <>
      {isLoading && <Loader />}

      <div className="page-wrapper">
        <div className="content">
          {/* Header Section */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-4">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Reports Overview</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Reports</li>
                  <li className="breadcrumb-item active">Overview</li>
                </ol>
              </nav>
            </div>

            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="me-2 mb-2">
                <Link
                  to={all_routes.ersReportsCreate}
                  state={{
                    reportType: { id: -1, name: "" },
                    upcomingReportType: upcomingReports,
                    allUsers: allUsers,
                  }}
                  className="btn btn-primary d-inline-flex align-items-center">
                  <i className="ti ti-circle-plus me-2"></i>
                  Create New Report
                </Link>
              </div>

              <div className="ms-2 head-icons">
                <CollapseHeader />
              </div>
            </div>
          </div>

          {/* First Row - Upcoming Reports (Full Width) */}
          <div className="card">
            <div className="card-header border-0 bg-white mb-0">
              <div className="d-flex align-items-center justify-content-between flex-wrap">
                <div className="d-flex align-items-center gap-3">
                  <h5 className="card-title mb-0">Upcoming Reports</h5>
                  <div className="d-flex gap-2">
                    <span className="d-flex btn btn-sm btn-primary">
                      {totalFilteredReports} Due
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body px-3 py-2">
              {totalFilteredReports > 0 ? (
                <>
                  <div className="mt-3 d-flex align-items-center justify-content-center gap-2">
                    {paginatedUpcomingReports.map((report, index) => (
                      <div
                        key={`${report.id}-${upcomingCurrentPage}-${index}`}
                        className="p-2">
                        <UpcomingReportCard
                          report={report}
                          upcomingReportType={upcomingReports}
                          allUsers={allUsers}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <Pagination
                        currentPage={upcomingCurrentPage}
                        totalPages={upcomingTotalPages}
                        totalItems={totalFilteredReports}
                        itemsPerPage={upcomingReportsPerPage}
                        onPageChange={handleUpcomingPageChange}
                        size="sm"
                        showInfo={true}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center pt-4 pb-5">
                  <i className="ti ti-checks text-success fs-40"></i>
                  <h6 className="mt-2 mb-1">All Caught Up!</h6>
                  <p className="text-muted small">
                    No upcoming reports at this time.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Filters Section */}
          <div className="card">
            <div className="card-header border-0">
              <div className="d-flex align-items-center justify-content-between">
                <div
                  onClick={toggleFilters}
                  className="d-flex gap-3 align-items-center"
                  style={{ cursor: "pointer" }}>
                  <h5 className="card-title mb-0 d-flex align-items-center gap-2">
                    <i className={`me-1 fs-28 ti ti-filter`}></i>
                    Filters
                  </h5>
                  <span
                    className={`btn btn-md ${activeFiltersCount > 0
                      ? "btn-warning text-white"
                      : "btn-secondary"
                      }`}>
                    {showFilters ? "Hide" : "Show"}
                    {activeFiltersCount > 0 && ` (${activeFiltersCount})`}
                    <i
                      className={`ms-1 ti ti-caret-${showFilters ? "up" : "down"
                        }`}></i>
                  </span>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <button
                    style={{ minWidth: "100px" }}
                    className="btn btn-info btn-md"
                    onClick={handleAllReportsSearch}>
                    <i className="ti ti-search me-1"></i>
                    Search
                  </button>
                  <button
                    style={{ minWidth: "100px" }}
                    className="btn btn-outline-danger btn-md"
                    onClick={handleClearAllReportsFilters}
                    disabled={activeFiltersCount === 0}>
                    <i className="ti ti-x me-1"></i>
                    Clear {activeFiltersCount > 0 && `(${activeFiltersCount})`}
                  </button>
                </div>
              </div>

              {/* Filters Section */}
              {showFilters && (
                <div className="mt-3 d-flex flex-wrap bg-secondary bg-opacity-10 align-items-end p-3 rounded-3 gap-3">
                  {/* Search by title  */}
                  <div style={{ minWidth: "210px" }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <label className="form-label small mb-0">
                        Search Reports
                      </label>
                      {searchTerm && (
                        <ClearFilterBtn onClick={clearSearchTerm} />
                      )}
                    </div>
                    <Input
                      className="mb-2 p-2"
                      placeholder="Search by title..."
                      value={searchTerm || ""}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      prefix={<i className="ti ti-search" />}
                    />
                  </div>

                  {/* Date Filters  */}
                  <div style={{ minWidth: "210px" }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <label className="form-label small mb-0">
                        Start Date
                      </label>
                      {reportsStartDate && (
                        <ClearFilterBtn onClick={clearStartDateFilters} />
                      )}
                    </div>
                    <DatePicker
                      style={{ width: "100%" }}
                      value={reportsStartDate}
                      onChange={(date: Dayjs | null) =>
                        setReportsStartDate(date)
                      }
                      placeholder="Start Date"
                      format="DD MMM YYYY"
                    />
                  </div>
                  <div style={{ minWidth: "210px" }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <label className="form-label small mb-0">End Date</label>
                      {reportsEndDate && (
                        <ClearFilterBtn onClick={clearEndDateFilters} />
                      )}
                    </div>
                    <DatePicker
                      style={{ width: "100%" }}
                      value={reportsEndDate}
                      onChange={(date: Dayjs | null) => setReportsEndDate(date)}
                      placeholder="End Date"
                      format="DD MMM YYYY"
                    />
                  </div>

                  {/* Filter by Frequency type */}
                  <div style={{ minWidth: "180px" }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <label className="form-label small mb-0">Frequency</label>
                      {reportFrequency && (
                        <ClearFilterBtn onClick={clearReportFrequency} />
                      )}
                    </div>
                    <Select
                      style={{ width: "100%" }}
                      className="mb-2"
                      placeholder="Select Frequency"
                      value={reportFrequency}
                      onChange={(value) => setReportFrequency(value)}
                      options={[
                        { label: "Daily", value: "daily" },
                        { label: "Weekly", value: "weekly" },
                        { label: "Monthly", value: "monthly" },
                      ]}
                    />
                  </div>

                  {/* Sort */}
                  <div style={{ minWidth: "250px" }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <label className="form-label small mb-0">Sort By</label>
                      {reportsSortBy && (
                        <ClearFilterBtn onClick={clearSortBy} />
                      )}
                    </div>
                    <Select
                      className="mb-2"
                      style={{ width: "100%" }}
                      placeholder="Sort By"
                      value={reportsSortBy}
                      onChange={(value) => setReportsSortBy(value)}
                      options={reportsSortOptions}
                    />
                  </div>

                  {/* Filter by Report type  */}
                  <div style={{ minWidth: "200px" }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <label className="form-label small mb-0">
                        Report Type
                      </label>
                      {reportTypeFilter && (
                        <ClearFilterBtn onClick={clearReportTypeFilter} />
                      )}
                    </div>
                    <Select
                      className="mb-2"
                      style={{ width: "100%" }}
                      placeholder="Select Report"
                      value={reportTypeFilter}
                      onChange={(value) => setReportTypeFilter(value)}
                      showSearch
                      filterOption={(input: any, option: any) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={[
                        ...reportTypes?.map((reportType: any) => ({
                          value: reportType.id,
                          label: reportType.name,
                        })),
                        { value: -1, label: "Custom Reports" },
                      ]}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Stats Row */}
          <div className="pt-0 d-flex flex-wrap align-items-center justify-content-between bg-none">
            <div className="col-lg-3 col-md-6">
              <Link
                to={"#"}
                data-bs-toggle="modal"
                data-bs-target="#reports-stats-info-modal"
                onClick={(e: any) => {
                  e.preventDefault();
                  handleStatsCardClick("total-stats");
                }}>
                <StatsCard
                  icon="ti-report-analytics"
                  iconColor="text-info"
                  title="Total Submitted"
                  value={stats?.summary?.total_reports || 0}
                  className="h-100"
                />
              </Link>
            </div>
            <div className="col-lg-3 col-md-6">
              <Link
                to={"#"}
                data-bs-toggle="modal"
                data-bs-target="#reports-stats-info-modal"
                onClick={(e: any) => {
                  e.preventDefault();
                  handleStatsCardClick("submitted-on-time-reports");
                }}>
                <StatsCard
                  icon="ti-circle-check"
                  iconColor="text-success"
                  title="Submitted On-Time"
                  value={stats?.summary?.submitted_on_time || 0}
                  className="h-100"
                />
              </Link>
            </div>
            <div className="col-lg-3 col-md-6">
              <Link
                to={"#"}
                data-bs-toggle="modal"
                data-bs-target="#reports-stats-info-modal"
                onClick={(e: any) => {
                  e.preventDefault();
                  handleStatsCardClick("submitted-late-reports");
                }}>
                <StatsCard
                  icon="ti-files"
                  iconColor="text-primary"
                  title="Submitted Late"
                  value={stats?.summary?.submitted_late || 0}
                  className="h-100"
                />
              </Link>
            </div>
            <div className="col-lg-3 col-md-6">
              <Link
                to={"#"}
                data-bs-toggle="modal"
                data-bs-target="#reports-stats-info-modal"
                onClick={(e: any) => {
                  e.preventDefault();
                  handleStatsCardClick("overdue-reports");
                }}>
                <StatsCard
                  icon="ti-alert-triangle"
                  iconColor="text-danger"
                  title="Due Reports"
                  value={stats?.summary?.overdue_reports || 0}
                  className="h-100"
                />
              </Link>
            </div>
          </div>

          {/* Bottom Section - All Reports */}
          <div className="card mt-3">
            <div className="card-header border-0">
              <div className="d-flex align-items-center justify-content-between">
                <h5 className="card-title mb-0">
                  <i className="ti ti-files text-primary me-2"></i>
                  All Reports
                </h5>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge badge-md bg-primary">
                    {allReportsPaginationData.totalItems} Total
                  </span>
                </div>
              </div>
            </div>

            <div className="card-body">
              {allReports.length > 0 ? (
                <>
                  <div className="reports-list">
                    {allReports.map((report) => (
                      <AllReportsCard
                        key={report.id}
                        report={report}
                        onDeleteReport={handleReportDelete}
                        onCreateTemplate={onCreateTemplate}
                        allUsers={allUsers}
                        upcomingReportType={upcomingReports}
                      />
                    ))}
                  </div>

                  {allReportsPaginationData.totalPages >= 1 && (
                    <div className="mt-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <Pagination
                          currentPage={allReportsPaginationData.currentPage}
                          totalPages={allReportsPaginationData.totalPages}
                          totalItems={allReportsPaginationData.totalItems}
                          itemsPerPage={allReportsPaginationData.itemsPerPage}
                          onPageChange={handleAllReportsPageChange}
                          size="sm"
                          showInfo={true}
                        />
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-5">
                  <i
                    className="ti ti-file-text text-muted"
                    style={{ fontSize: "3rem" }}></i>
                  <h5 className="mt-3 mb-2">No Reports Found</h5>
                  <p className="text-muted mb-3">
                    {Object.values(allReportsFilters).some((f) => f !== null)
                      ? "Try adjusting your filters to see more results"
                      : "Create your first report to get started"}
                  </p>
                  {Object.values(allReportsFilters).some((f) => f !== null) ? (
                    <button
                      className="btn btn-outline-primary"
                      onClick={handleClearAllReportsFilters}>
                      <i className="ti ti-filter-off me-2"></i>
                      Clear Filters
                    </button>
                  ) : (
                    <Link
                      to={all_routes.ersReportsCreate}
                      state={{
                        reportType: { id: -1, name: "" },
                        upcomingReportType: upcomingReports,
                        allUsers: allUsers,
                      }}
                      className="btn btn-primary d-inline-flex align-items-center">
                      <i className="ti ti-circle-plus me-2"></i>
                      Create New Report
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer Section */}
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3 mt-4">
          <p className="mb-0">2025 © CMS.</p>
          <p className="mb-0">
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              ZYVO
            </Link>
          </p>
        </div>
      </div>

      <QuickInfoModal
        title={modalInfo}
        dataSource={modalData}
        columns={modalDataColumns}
      />
    </>
  );
};

export default MyReports;
